/**
 * Normalisation de l'URL du logo d'une organisation.
 *
 * Le logo est déposé manuellement sur Google Drive puis son adresse est collée
 * dans le portail admin. Or un lien de PARTAGE Drive
 * (`https://drive.google.com/file/d/<ID>/view?usp=sharing`) renvoie une page
 * HTML, pas une image : posé dans un `<img src>` il ne s'affiche jamais. Seule
 * la forme `thumbnail?id=<ID>` sert l'octet de l'image.
 *
 * On normalise donc à l'écriture (mutation super-admin) ET à la saisie (preview
 * du formulaire), pour que ce qui est enregistré soit directement affichable.
 */

/** Largeur demandée à Drive. Le logo est rendu au plus à ~192px de large. */
const DRIVE_THUMBNAIL_WIDTH = 400;

/**
 * Extrait l'identifiant de fichier Drive d'une URL, quelle que soit sa forme
 * connue. Renvoie `null` si l'URL n'est pas une URL Drive reconnaissable.
 */
function extraireIdDrive(url: string): string | null {
  const patterns = [
    /\/file\/d\/([\w-]+)/, // https://drive.google.com/file/d/<ID>/view
    /[?&]id=([\w-]+)/, // …/uc?export=view&id=<ID> et …/thumbnail?id=<ID>
    /\/d\/([\w-]+)/, // formes courtes /d/<ID>
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m?.[1]) return m[1];
  }
  return null;
}

/**
 * Renvoie une URL directement utilisable dans un `<img src>`.
 *
 * - URL Google Drive reconnue → réécrite en `thumbnail?id=…` ;
 * - toute autre URL (CDN, site du client) → renvoyée telle quelle, après trim ;
 * - chaîne vide / blancs → `undefined`, pour effacer le champ en base.
 */
export function normaliserUrlLogo(url: string | undefined): string | undefined {
  const brut = url?.trim();
  if (!brut) return undefined;

  if (brut.includes("drive.google.com") || brut.includes("docs.google.com")) {
    const id = extraireIdDrive(brut);
    if (id) {
      return `https://drive.google.com/thumbnail?id=${id}&sz=w${DRIVE_THUMBNAIL_WIDTH}`;
    }
  }

  return brut;
}

/**
 * Vrai si l'URL est un lien Drive dont on n'a PAS su extraire l'identifiant :
 * le formulaire admin avertit alors que l'image ne s'affichera pas. Distinct de
 * « URL non-Drive », qui est un cas parfaitement valide.
 */
export function estLienDriveNonExploitable(url: string): boolean {
  const brut = url.trim();
  if (!brut) return false;
  const estDrive =
    brut.includes("drive.google.com") || brut.includes("docs.google.com");
  return estDrive && extraireIdDrive(brut) === null;
}
